import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, IconButton, Slider, ToggleButtonGroup, ToggleButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Collapse } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import StopIcon from '@mui/icons-material/Stop';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import SkipPreviousIcon from '@mui/icons-material/SkipPrevious';
import TableChartIcon from '@mui/icons-material/TableChart';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import DataUsageIcon from '@mui/icons-material/DataUsage';
import Point from "@arcgis/core/geometry/Point";
import Graphic from "@arcgis/core/Graphic";
import SimpleMarkerSymbol from "@arcgis/core/symbols/SimpleMarkerSymbol";

function RoutePlayback({
  mapView,
  routeData,
  onPositionChange = () => { }
}) {
  // Estados
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentPosition, setCurrentPosition] = useState(0);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [mode, setMode] = useState('time'); // 'time' o 'data'
  const [showTable, setShowTable] = useState(false);
  const [vehicleGraphic, setVehicleGraphic] = useState(null);

  // Referencias
  const animationRef = useRef(null);
  const startTimeRef = useRef(null);
  const lastFrameTimeRef = useRef(null);

  // Calcular la duración total
  // const totalDuration = routeData?.coordinates?.length 
  //   ? (new Date(routeData.coordinates[routeData.coordinates.length-1].time) - new Date(routeData.coordinates[0].time)) / 1000
  //   : 0;

  // Opciones de velocidad disponibles
  const speedOptions = [1, 10, 30, 50, 70, 100];

  // Crear el vehículo en el mapa
  useEffect(() => {
    if (mapView && routeData?.coordinates?.length) {
      // Símbolo para el vehículo
      const vehicleSymbol = new SimpleMarkerSymbol({
        color: [1, 165, 20],
        size: 12,
        outline: {
          color: [255, 255, 255],
          width: 2
        },
        style: "triangle"
      });

      // Usar la primera coordenada para la posición inicial
      const initialPosition = routeData.coordinates[0];
      const point = new Point({
        x: initialPosition.longitude,
        y: initialPosition.latitude,
        spatialReference: { wkid: 4326 }
      });

      // Crear el gráfico para el vehículo
      const graphic = new Graphic({
        geometry: point,
        symbol: vehicleSymbol,
        attributes: {
          type: 'vehicle'
        }
      });

      // Añadir a la capa (asumiendo que se pasa como referencia, no se está creando aquí)
      if (mapView.map) {
        // Verificar si ya existe una capa vehicleLayer
        let vehicleLayer = mapView.map.layers.find(layer => layer.id === 'vehiclePlaybackLayer');

        // Si no existe, crearla
        if (!vehicleLayer) {
          const GraphicsLayer = require('@arcgis/core/layers/GraphicsLayer').default;
          vehicleLayer = new GraphicsLayer({
            id: 'vehiclePlaybackLayer'
          });
          mapView.map.add(vehicleLayer);
        } else {
          // Limpiar gráficos existentes
          vehicleLayer.removeAll();
        }

        // Añadir el gráfico del vehículo
        vehicleLayer.add(graphic);
        setVehicleGraphic(graphic);
      }

      // Verificar si las coordenadas están ordenadas
      if (routeData.coordinates.length > 1) {
        console.log('Verificando orden de coordenadas...');
        let desordenadas = false;
        for (let i = 0; i < routeData.coordinates.length - 1; i++) {
          const punto1 = routeData.coordinates[i];
          const punto2 = routeData.coordinates[i + 1];
          const timestamp1 = new Date(punto1.time || punto1.timestamp || 0).getTime();
          const timestamp2 = new Date(punto2.time || punto2.timestamp || 0).getTime();

          if (timestamp1 > timestamp2) {
            console.warn(`Coordenadas desordenadas detectadas en índices ${i} y ${i + 1}`);
            desordenadas = true;
            break;
          }
        }
        if (!desordenadas) {
          console.log('Coordenadas correctamente ordenadas por tiempo');
        }
      }
    }

    return () => {
      // Limpiar al desmontar
      if (mapView && mapView.map) {
        const vehicleLayer = mapView.map.layers.find(layer => layer.id === 'vehiclePlaybackLayer');
        if (vehicleLayer) {
          vehicleLayer.removeAll();
        }
      }
    };
  }, [mapView, routeData]);

  // Referencia para almacenar las posiciones anteriores (para suavizado)
  const lastPositionsRef = useRef([]);
  const MAX_POSITIONS_HISTORY = 3; // Número de posiciones anteriores para promediar

  // Referencia para la dirección de movimiento
  const lastDirectionRef = useRef({ longitude: 0, latitude: 0 });

  // Función de animación - modificada para mantener movimiento unidireccional
  const animate = (timestamp) => {
    if (!startTimeRef.current) {
      startTimeRef.current = timestamp;
      lastFrameTimeRef.current = timestamp;
    }

    // Limitar el tiempo transcurrido a un máximo razonable para evitar saltos grandes
    // cuando la pestaña del navegador estuvo inactiva
    const elapsed = Math.min(timestamp - lastFrameTimeRef.current, 100);
    const speedFactor = playbackSpeed; // Factor de velocidad

    if (routeData?.coordinates?.length && vehicleGraphic) {
      // Calcular el avance basado en el tiempo transcurrido y la velocidad
      const newPosition = Math.min(
        currentPosition + (elapsed / 1000) * speedFactor,
        routeData.coordinates.length - 1
      );

      // Si llegamos al final, detener la animación
      if (newPosition >= routeData.coordinates.length - 1) {
        setCurrentPosition(routeData.coordinates.length - 1);
        setIsPlaying(false);
        return;
      }

      // Actualizar la posición actual
      setCurrentPosition(newPosition);

      // Obtener las coordenadas para la interpolación
      const index = Math.floor(newPosition);
      const nextIndex = Math.min(index + 1, routeData.coordinates.length - 1);
      const fraction = newPosition - index;

      const current = routeData.coordinates[index];
      const next = routeData.coordinates[nextIndex];

      // Interpolar la posición con dirección directa entre puntos
      const longitude = current.longitude + (next.longitude - current.longitude) * fraction;
      const latitude = current.latitude + (next.latitude - current.latitude) * fraction;

      // Calcular la dirección de movimiento actual
      const currentDirection = {
        longitude: next.longitude - current.longitude,
        latitude: next.latitude - current.latitude
      };

      // Normalizar la dirección (convertir a vector unitario)
      const magnitude = Math.sqrt(
        currentDirection.longitude * currentDirection.longitude +
        currentDirection.latitude * currentDirection.latitude
      );

      if (magnitude > 0) {
        currentDirection.longitude /= magnitude;
        currentDirection.latitude /= magnitude;
      }

      // Almacenar la nueva posición en el historial con un enfoque direccional
      const newHistoryEntry = {
        longitude,
        latitude,
        // Almacenar también la dirección para análisis
        direction: { ...currentDirection }
      };

      // Adaptar la cantidad de suavizado según la velocidad
      // A mayor velocidad, menos suavizado para evitar el efecto rebote
      const adaptiveHistorySize = Math.max(
        1, // Mínimo 1 posición (sin suavizado)
        Math.ceil(MAX_POSITIONS_HISTORY / Math.sqrt(speedFactor))
      );

      lastPositionsRef.current.push(newHistoryEntry);
      // Mantener solo el número adaptado de posiciones
      while (lastPositionsRef.current.length > adaptiveHistorySize) {
        lastPositionsRef.current.shift();
      }

      // Verificar coherencia de dirección - no permitir movimientos en direcciones opuestas
      let directionallyValidPositions = [...lastPositionsRef.current];
      if (directionallyValidPositions.length > 1) {
        // Filtrar posiciones que van en sentido contrario a la dirección principal
        const mainDirection = currentDirection;

        // Solo conservar posiciones que van aproximadamente en la misma dirección
        directionallyValidPositions = directionallyValidPositions.filter((pos, idx) => {
          if (idx === directionallyValidPositions.length - 1) return true; // Siempre mantener la última

          // Si tiene información de dirección, comprobar que sea coherente
          if (pos.direction) {
            const dotProduct =
              pos.direction.longitude * mainDirection.longitude +
              pos.direction.latitude * mainDirection.latitude;

            // Si el producto escalar es negativo, las direcciones son opuestas
            return dotProduct >= 0;
          }

          return true;
        });
      }

      // Calcular posición suavizada usando las posiciones válidas direccionalmente
      let smoothedLongitude = longitude;
      let smoothedLatitude = latitude;

      if (directionallyValidPositions.length > 1) {
        // Aplicar suavizado solo si tenemos suficientes posiciones históricas válidas
        let totalWeight = 0;
        let weightedLongSum = 0;
        let weightedLatSum = 0;

        // Dar más peso a las posiciones más recientes
        directionallyValidPositions.forEach((pos, idx) => {
          const weight = idx + 1; // Peso incremental (1, 2, 3...)
          totalWeight += weight;
          weightedLongSum += pos.longitude * weight;
          weightedLatSum += pos.latitude * weight;
        });

        if (totalWeight > 0) {
          smoothedLongitude = weightedLongSum / totalWeight;
          smoothedLatitude = weightedLatSum / totalWeight;

          // Verificación extra: asegurarse que la posición suavizada no está "retrocediendo"
          // Si la posición suavizada va en dirección opuesta a la dirección actual, no aplicar suavizado
          const smoothedDirection = {
            longitude: smoothedLongitude - current.longitude,
            latitude: smoothedLatitude - current.latitude
          };

          const dotProduct =
            smoothedDirection.longitude * currentDirection.longitude +
            smoothedDirection.latitude * currentDirection.latitude;

          // Si el producto escalar es negativo, usar la posición interpolada sin suavizar
          if (dotProduct < 0) {
            smoothedLongitude = longitude;
            smoothedLatitude = latitude;
          }
        }
      }

      // Actualizar la referencia de dirección
      lastDirectionRef.current = currentDirection;

      // Actualizar la posición del vehículo con la posición suavizada
      vehicleGraphic.geometry = new Point({
        x: smoothedLongitude,
        y: smoothedLatitude,
        spatialReference: { wkid: 4326 }
      });

      // Centrar el mapa en la posición actual si es necesario, pero con menos frecuencia
      // para evitar sacudidas y solo cuando el punto se acerca al borde de la vista
      const screenPoint = mapView.toScreen(vehicleGraphic.geometry);
      const viewportWidth = mapView.width;
      const viewportHeight = mapView.height;

      // Solo mover el mapa si el vehículo se acerca a los bordes (margen del 20%)
      const margin = 0.2;
      const needsRecentering =
        screenPoint.x < viewportWidth * margin ||
        screenPoint.x > viewportWidth * (1 - margin) ||
        screenPoint.y < viewportHeight * margin ||
        screenPoint.y > viewportHeight * (1 - margin);

      if (needsRecentering) {
        mapView.goTo({
          target: vehicleGraphic.geometry,
          scale: mapView.scale // Mantener el nivel de zoom actual
        }, {
          duration: 500, // Movimiento más lento y suave
          easing: 'ease-in-out'
        });
      }

      // Notificar sobre el cambio de posición (para sincronizar con la tabla)
      onPositionChange(Math.floor(newPosition));
    }

    lastFrameTimeRef.current = timestamp;

    // Continuar la animación si sigue reproduciendo
    if (isPlaying) {
      animationRef.current = requestAnimationFrame(animate);
    }
  };

  // Función auxiliar para limpiar la historia de posiciones
  const resetPositionHistory = () => {
    lastPositionsRef.current = [];
    lastDirectionRef.current = { longitude: 0, latitude: 0 };
  };

  // Efecto para iniciar/detener la animación
  useEffect(() => {
    if (isPlaying) {
      animationRef.current = requestAnimationFrame(animate);
    } else if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
      startTimeRef.current = null;
    }

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isPlaying, playbackSpeed, currentPosition]);

  // Manejador de play/pause (actualizado)
  const handlePlayPause = () => {
    // Si cambiamos de pausa a reproducción, reset del historial
    if (!isPlaying) {
      resetPositionHistory();
    }
    setIsPlaying(!isPlaying);
  };

  // Manejador de stop (reset) (actualizado)
  const handleStop = () => {
    setIsPlaying(false);
    setCurrentPosition(0);
    resetPositionHistory();

    // Mover el vehículo a la posición inicial
    if (vehicleGraphic && routeData?.coordinates?.length) {
      const initial = routeData.coordinates[0];
      vehicleGraphic.geometry = new Point({
        x: initial.longitude,
        y: initial.latitude,
        spatialReference: { wkid: 4326 }
      });

      // Centrar el mapa en la posición inicial
      mapView.goTo({
        target: vehicleGraphic.geometry
      });
    }
  };

  // Manejador para saltar a una posición específica (actualizado)
  const handlePositionChange = (event, newValue) => {
    setCurrentPosition(newValue);
    resetPositionHistory();

    // Actualizar la posición del vehículo
    if (vehicleGraphic && routeData?.coordinates?.length) {
      const position = routeData.coordinates[Math.floor(newValue)];
      vehicleGraphic.geometry = new Point({
        x: position.longitude,
        y: position.latitude,
        spatialReference: { wkid: 4326 }
      });

      // Centrar el mapa con una transición más suave
      mapView.goTo({
        target: vehicleGraphic.geometry
      }, {
        duration: 500,
        easing: 'ease-in-out'
      });

      // Notificar sobre el cambio de posición
      onPositionChange(Math.floor(newValue));
    }
  };

  // Manejador para cambiar la velocidad de reproducción
  const handleSpeedChange = (newSpeed) => {
    setPlaybackSpeed(newSpeed);
  };

  // Manejador para cambiar el modo (tiempo/datos)
  const handleModeChange = (event, newMode) => {
    if (newMode !== null) {
      setMode(newMode);
    }
  };

  // Manejador para mostrar/ocultar la tabla
  const handleTableToggle = () => {
    setShowTable(!showTable);
  };

  // Manejador para saltar al siguiente punto (mejorado)
  const handleSkipNext = () => {
    const nextPosition = Math.min(Math.floor(currentPosition) + 1, routeData.coordinates.length - 1);
    setCurrentPosition(nextPosition);

    // Actualizar la posición del vehículo
    if (vehicleGraphic && routeData?.coordinates?.length) {
      const position = routeData.coordinates[nextPosition];
      vehicleGraphic.geometry = new Point({
        x: position.longitude,
        y: position.latitude,
        spatialReference: { wkid: 4326 }
      });

      // Centrar el mapa con una transición más suave
      mapView.goTo({
        target: vehicleGraphic.geometry
      }, {
        duration: 500,
        easing: 'ease-in-out'
      });

      // Notificar sobre el cambio de posición
      onPositionChange(nextPosition);
    }
  };

  // Manejador para saltar al punto anterior (mejorado)
  const handleSkipPrevious = () => {
    const prevPosition = Math.max(Math.floor(currentPosition) - 1, 0);
    setCurrentPosition(prevPosition);

    // Actualizar la posición del vehículo
    if (vehicleGraphic && routeData?.coordinates?.length) {
      const position = routeData.coordinates[prevPosition];
      vehicleGraphic.geometry = new Point({
        x: position.longitude,
        y: position.latitude,
        spatialReference: { wkid: 4326 }
      });

      // Centrar el mapa con una transición más suave
      mapView.goTo({
        target: vehicleGraphic.geometry
      }, {
        duration: 500,
        easing: 'ease-in-out'
      });

      // Notificar sobre el cambio de posición
      onPositionChange(prevPosition);
    }
  };

  // Manejador para click en una fila de la tabla
  const handleRowClick = (index) => {
    setCurrentPosition(index);

    // Actualizar la posición del vehículo
    if (vehicleGraphic && routeData?.coordinates?.length) {
      const position = routeData.coordinates[index];
      vehicleGraphic.geometry = new Point({
        x: position.longitude,
        y: position.latitude,
        spatialReference: { wkid: 4326 }
      });

      // Centrar el mapa en la posición seleccionada
      mapView.goTo({
        target: vehicleGraphic.geometry
      });
    }
  };

  // Agregar un efecto para inspeccionar la estructura de datos
  useEffect(() => {
    if (routeData?.coordinates?.length > 0) {
      // Inspeccionar la estructura del primer punto para ver qué campos contiene
      console.log('Estructura de un punto de coordenadas:', routeData.coordinates[0]);
      console.log('Todos los keys disponibles:', Object.keys(routeData.coordinates[0]));
    }
  }, [routeData]);

  // Formatear tiempo para mostrar
  const formatTime = (timestamp) => {
    //console.log('Raw timestamp value:', timestamp, typeof timestamp);

    // Si no hay timestamp, buscar otros campos posibles
    if (timestamp === undefined) {
      return 'Sin datos de tiempo';
    }

    const date = new Date(timestamp);
    //console.log('Parsed date object:', date, 'isValid:', !isNaN(date.getTime()));

    if (isNaN(date.getTime())) {
      return 'Fecha inválida';
    }

    // Formatear como YYYY-MM-DD HH:MM:SS
    const pad = (num) => num.toString().padStart(2, '0');
    const year = date.getFullYear();
    const month = pad(date.getMonth() + 1);
    const day = pad(date.getDate());
    const hours = pad(date.getHours());
    const minutes = pad(date.getMinutes());
    const seconds = pad(date.getSeconds());

    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  };

  return (
    <Box
      sx={{
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        bgcolor: 'rgba(33, 33, 33, 0.9)',
        color: 'white',
        zIndex: 1000,
        p: 1,
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      {/* Controles principales */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: showTable ? 1 : 0 }}>
        {/* Botón para tabla */}
        <IconButton
          color={showTable ? "primary" : "default"}
          onClick={handleTableToggle}
          sx={{ color: showTable ? 'primary.main' : 'white' }}
        >
          <TableChartIcon />
        </IconButton>

        {/* Toggle de modo */}
        <ToggleButtonGroup
          value={mode}
          exclusive
          onChange={handleModeChange}
          size="small"
          sx={{
            mx: 1,
            '& .MuiToggleButton-root': {
              color: 'white',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              '&.Mui-selected': {
                bgcolor: 'primary.main',
                color: 'white'
              }
            }
          }}
        >
          <ToggleButton value="time" aria-label="time mode">
            <AccessTimeIcon />
          </ToggleButton>
          <ToggleButton value="data" aria-label="data mode">
            <DataUsageIcon />
          </ToggleButton>
        </ToggleButtonGroup>

        {/* Controles de reproducción */}
        <IconButton onClick={handleSkipPrevious} sx={{ color: 'white' }}>
          <SkipPreviousIcon />
        </IconButton>

        <IconButton onClick={handlePlayPause} sx={{ color: 'white' }}>
          {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
        </IconButton>

        <IconButton onClick={handleStop} sx={{ color: 'white' }}>
          <StopIcon />
        </IconButton>

        <IconButton onClick={handleSkipNext} sx={{ color: 'white' }}>
          <SkipNextIcon />
        </IconButton>

        {/* Velocidad de reproducción */}
        <Box sx={{ display: 'flex', alignItems: 'center', ml: 2 }}>
          <Typography variant="body2" sx={{ mr: 1 }}>
            Velocidad:
          </Typography>
          <ToggleButtonGroup
            value={playbackSpeed}
            exclusive
            onChange={(e, val) => val !== null && handleSpeedChange(val)}
            size="small"
            sx={{
              '& .MuiToggleButton-root': {
                py: 0,
                px: 1,
                minWidth: '36px',
                color: 'white',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                '&.Mui-selected': {
                  bgcolor: 'primary.main',
                  color: 'white'
                }
              }
            }}
          >
            {speedOptions.map(speed => (
              <ToggleButton key={speed} value={speed} aria-label={`speed x${speed}`}>
                X{speed}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>

        {/* Información de tiempo actual */}
        <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center' }}>
          <Typography variant="body2" sx={{ mx: 1 }}>
            {routeData?.coordinates?.length &&
              formatTime(routeData.coordinates[Math.floor(currentPosition)]?.time)}
          </Typography>
        </Box>
      </Box>

      {/* Slider de progreso */}
      <Slider
        value={currentPosition}
        min={0}
        max={routeData?.coordinates?.length ? routeData.coordinates.length - 1 : 100}
        step={0.01}
        onChange={handlePositionChange}
        sx={{
          mx: 2,
          '.MuiSlider-thumb': {
            width: 12,
            height: 12,
          },
          '.MuiSlider-rail': {
            bgcolor: 'rgba(255, 255, 255, 0.3)'
          },
          '.MuiSlider-track': {
            bgcolor: 'primary.main'
          }
        }}
      />

      {/* Tabla de datos */}
      <Collapse in={showTable} timeout="auto" unmountOnExit>
        <TableContainer
          component={Paper}
          sx={{
            maxHeight: 200,
            bgcolor: 'background.paper',
            '.MuiTableRow-root.selected': {
              bgcolor: 'primary.light'
            }
          }}
        >
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell>Timestamp</TableCell>
                <TableCell>Latitud</TableCell>
                <TableCell>Longitud</TableCell>
                <TableCell>Velocidad</TableCell>
                {mode === 'data' && (
                  <>
                    <TableCell>ID</TableCell>
                  </>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {routeData?.coordinates?.map((point, index) => {
                // Intentar encontrar el campo de tiempo correcto
                const timeField = point.time || point.timestamp || point.date || null;

                return (
                  <TableRow
                    key={index}
                    onClick={() => handleRowClick(index)}
                    className={Math.floor(currentPosition) === index ? 'selected' : ''}
                    sx={{
                      cursor: 'pointer',
                      bgcolor: Math.floor(currentPosition) === index ? 'rgba(25, 118, 210, 0.2)' : 'transparent',
                      '&:hover': {
                        bgcolor: 'rgba(0, 0, 0, 0.1)'
                      }
                    }}
                  >
                    <TableCell>{formatTime(timeField)}</TableCell>
                    <TableCell>{point.latitude.toFixed(6)}</TableCell>
                    <TableCell>{point.longitude.toFixed(6)}</TableCell>
                    <TableCell>{point.speed || 0} km/h</TableCell>
                    {mode === 'data' && (
                      <>
                        <TableCell>{point.id || index}</TableCell>
                      </>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Collapse>
    </Box>
  );
}

export default RoutePlayback; 